import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { StarAiFill } from '@lyearn/icons';
import { EmptyState } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Divider, Stack, Typography } from '@lyearn/ui';
import AsyncView from '@/components/AsyncView';
import VirtualizedList from '@/components/VirtualizedList';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import InsightRow from '@/modules/homepage/views/Homepage/widgets/HomepageAIRecommendationsWidget/components/InsightRow';
import InsightRowMini from '@/modules/homepage/views/Homepage/widgets/HomepageAIRecommendationsWidget/components/InsightRowMini';
import useRecommendationsActions from '@/modules/homepage/views/Homepage/widgets/HomepageAIRecommendationsWidget/hooks/useRecommendationsActions';
import { getSelfAiRecommendationsFilters } from '@/modules/homepage/views/Homepage/widgets/HomepageAIRecommendationsWidget/utils';

import useRecommendedActions from './hooks/useRecommendedActions';

function ActionsList({ showFullView = false }: { showFullView?: boolean }) {
  const { t } = useTranslation(['Common', 'Notifications']);
  const { id: currentUserId } = useUserContext();
  const { recommendations, fetchMore, isFetching } = useRecommendedActions({
    filters: {
      ...getSelfAiRecommendationsFilters({
        currentUserId,
      }),
    },
  });

  const { updatedRecommendations, updateRecommendationStatus } = useRecommendationsActions({
    recommendations,
  });

  // Memoized item renderer for VirtualizedList
  const itemContent = React.useCallback(
    (index: number, recommendation: any) => (
      <React.Fragment key={recommendation.id}>
        <Divider className={cn(index === 0 ? 'hidden' : '')} orientation="horizontal" />
        {!showFullView ? (
          <InsightRowMini
            recommendation={recommendation}
            updateRecommendationStatus={updateRecommendationStatus}
          />
        ) : (
          <InsightRow
            index={index}
            recommendation={recommendation}
            updateRecommendationStatus={updateRecommendationStatus}
          />
        )}
      </React.Fragment>
    ),
    [showFullView, updateRecommendationStatus],
  );

  const computeItemKey = React.useCallback(
    (index: number, recommendation: any) => recommendation?.id || `recommendation-${index}`,
    [],
  );

  return (
    <Stack className="w-full" direction="col" gapY={4}>
      {/* <Stack
        alignItems="center"
        className="mt-8 px-20 w-full"
        direction="row"
        justifyContent="between">
        {!!updatedRecommendations.length &&
          PillsList.map((pill) => (
            <Box
              key={pill.value}
              className={cn(
                'px-12 py-4 rounded-24 bg-bg-tertiary border',
                selectedPills.includes(pill.value) ? 'border-border-field' : 'border-transparent',
              )}
              role="button"
              onClick={() => onSelectPill(pill.value)}>
              <Typography color="text-text-paragraph" variant="body-short-02">
                {pill.label}
              </Typography>
            </Box>
          ))}
      </Stack> */}
      <AsyncView
        data={updatedRecommendations}
        fetching={isFetching}
        renderData={(updatedRecommendations) => (
          <VirtualizedList
            className={cn('w-full h-full px-0')}
            computeItemKey={computeItemKey}
            data={updatedRecommendations}
            endReached={fetchMore}
            itemContent={itemContent}
          />
        )}
        renderEmptyState={() => (
          <Stack
            alignItems="center"
            className="w-full h-full"
            direction="col"
            justifyContent="center">
            <EmptyState
              Icon={StarAiFill}
              description=""
              title={
                <Typography color="text-text-placeholder" variant="label-02">
                  {t('Common:Notifications.NoAIActionsToSee')}
                </Typography>
              }
            />
          </Stack>
        )}
      />
    </Stack>
  );
}

export default React.memo(ActionsList);
